import { Link } from "react-router-dom";
import { ScrollReveal } from "../hooks/useScrollReveal";
import { FileText, ArrowLeft } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="min-h-screen" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-cyan-400 no-underline mb-6"><ArrowLeft className="w-3 h-3" /> Back</Link>
        <ScrollReveal>
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-6 h-6 text-cyan-400" />
            <h1 className="font-outfit text-2xl font-semibold text-foreground">Terms of Service</h1>
          </div>
          <p className="text-xs text-muted-foreground mb-8">Effective: January 1, 2026 &middot; Last updated: April 17, 2026</p>
        </ScrollReveal>

        <div className="bg-card border border-border/60 rounded-2xl p-8 space-y-8 text-sm text-foreground/80 leading-relaxed">
          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">1. Acceptance of Terms</h2>
            <p>By accessing or using App-Mi.ai ("the Service"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the Service. These terms apply to all users, including visitors, registered users, and subscribers.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">2. Account Responsibilities</h2>
            <ul className="list-disc ml-5 space-y-1">
              <li>You must provide accurate and complete information during registration</li>
              <li>You are responsible for maintaining the security of your password</li>
              <li>You must be at least 13 years of age to use the Service</li>
              <li>You are responsible for all activity under your account</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">3. AI Assistants</h2>
            <p className="mb-2">App-Mi.ai provides AI-powered assistants for informational and entertainment purposes. Important disclaimers:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>AI responses are generated by machine learning models and may contain errors</li>
              <li>AI assistants are <strong>not substitutes for professional advice</strong> (medical, legal, financial, or otherwise)</li>
              <li>Do not rely solely on AI output for important decisions</li>
              <li>AI assistants may occasionally generate inappropriate or inaccurate content</li>
              <li>You are solely responsible for how you use AI-generated content</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">4. Content Ownership</h2>
            <ul className="list-disc ml-5 space-y-1">
              <li>You retain ownership of all content you create on App-Mi.ai (posts, comments, messages)</li>
              <li>By posting content publicly, you grant App-Mi.ai a non-exclusive, worldwide license to display, distribute, and promote your content on the platform</li>
              <li>AI-generated content is provided to you for your personal use</li>
              <li>You may export your data at any time through Settings &rarr; Privacy</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">5. Prohibited Uses</h2>
            <p className="mb-2">You agree not to use App-Mi.ai to:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Harass, bully, or threaten other users</li>
              <li>Post illegal, defamatory, or obscene content</li>
              <li>Spam or send unsolicited messages</li>
              <li>Impersonate another person or entity</li>
              <li>Distribute malware or attempt to compromise the platform</li>
              <li>Attempt to circumvent AI safety measures or content filters</li>
              <li>Use the Service for any purpose that violates applicable law</li>
              <li>Scrape, mine, or extract data from the platform without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">6. Subscriptions & Billing</h2>
            <ul className="list-disc ml-5 space-y-1">
              <li>Some features require a paid subscription</li>
              <li>Subscriptions renew automatically unless cancelled before the renewal date</li>
              <li>Prices may change with 30 days notice</li>
              <li>Refunds are handled on a case-by-case basis</li>
              <li>Free tier users may experience rate limits and feature restrictions</li>
            </ul>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">7. Intellectual Property</h2>
            <p>The App-Mi.ai platform, including its design, code, branding, and documentation, is the intellectual property of App-Mi.ai. You may not copy, modify, distribute, or reverse-engineer any part of the Service without written permission.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">8. Limitation of Liability</h2>
            <p>App-Mi.ai is provided "as is" without warranties of any kind. We are not liable for any indirect, incidental, special, or consequential damages arising from your use of the Service, including but not limited to: loss of data, loss of profits, or reliance on AI-generated content.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">9. Termination</h2>
            <p>We reserve the right to suspend or terminate your account if you violate these Terms. You may delete your account at any time through Settings &rarr; Privacy. Upon termination, your data will be handled according to our Privacy Policy.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">10. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with applicable law, without regard to conflicts of law principles. Any disputes arising from these Terms shall be resolved through binding arbitration.</p>
          </section>

          <section>
            <h2 className="font-outfit text-lg font-medium text-foreground mb-2">11. Contact</h2>
            <p>For questions about these Terms of Service:</p>
            <p className="mt-1"><strong>Email:</strong> legal@app-mi.ai</p>
          </section>
        </div>
      </div>
    </div>
  );
}
